import { createApp } from "vue";
import App from "./App.vue";
import router from "./router";
import ApiService from "@/services/api.service";
import { IonicVue } from "@ionic/vue";
import i18n from "./i18n";
import useAuth from "@/componentables/auth";
import Echo from "laravel-echo";
import Pusher from "pusher-js";
import * as Sentry from "@sentry/capacitor";
import * as SentryVue from "@sentry/vue";
import Clarity from "@microsoft/clarity";
import { useConsent } from "@/composables/useConsent";

/* Core CSS required for Ionic components to work properly */
import "@ionic/vue/css/core.css";

/* Basic CSS for apps built with Ionic */
import "@ionic/vue/css/normalize.css";
import "@ionic/vue/css/structure.css";
import "@ionic/vue/css/typography.css";

/* Optional CSS utils that can be commented out */
import "@ionic/vue/css/padding.css";
import "@ionic/vue/css/float-elements.css";
import "@ionic/vue/css/text-alignment.css";
import "@ionic/vue/css/text-transformation.css";
import "@ionic/vue/css/flex-utils.css";
import "@ionic/vue/css/display.css";

/* Ionic Dark Mode - class based */
import "@ionic/vue/css/palettes/dark.class.css";

/* Theme variables */
import "./assets/scss/main.scss";
import { LiveUpdate } from "@capawesome/capacitor-live-update";
import { Capacitor } from "@capacitor/core";

import { useStaticData } from "@/composables/useStaticData";
import { useAttribution } from "@/composables/useAttribution";

import { installDebugLog, logStep } from "@/composables/useDebugLog";

installDebugLog();
logStep("main.ts top-level reached");

/* plugins */
ApiService.init(import.meta.env.VUE_APP_API_URL);

const DYNAMIC_IMPORT_RELOAD_KEY = "dynamic-import-reload-attempted";

function shouldHandleDynamicImportError(error: unknown): boolean {
    const message =
        error instanceof Error
            ? error.message
            : typeof error === "string"
              ? error
              : "";

    return /Failed to fetch dynamically imported module|Importing a module script failed|ChunkLoadError/i.test(
        message,
    );
}

/* Preload static data for instant page rendering */
useStaticData().preloadStaticData();

declare global {
    interface Window {
        Pusher: typeof Pusher; // You can replace `any` with the actual type if you know it.
        Echo: Echo<"reverb">;
    }
}

// make this ts set this on window
window.Pusher = Pusher;
window.Echo = new Echo({
    broadcaster: "reverb",
    key: import.meta.env.VUE_APP_REVERB_APP_KEY ?? "qyee9aku6sr5uknt1ojo",
    wsHost:
        import.meta.env.VUE_APP_REVERB_HOST ??
        "ws." +
            import.meta.env.VUE_APP_API_URL.replace(/^https?:\/\//, "").replace(
                /^http?:\/\//,
                "",
            ),
    wsPort: import.meta.env.VUE_APP_REVERB_PORT ?? 443,
    wssPort: import.meta.env.VUE_APP_REVERB_PORT ?? 443,
    forceTLS: (import.meta.env.VUE_APP_REVERB_SCHEME ?? "https") === "https",
    enabledTransports: ["ws", "wss"],
});

async function safeAwait<T>(
    promise: Promise<T>,
    label: string,
    timeoutMs = 5000,
): Promise<T | null> {
    try {
        return await Promise.race([
            promise,
            new Promise<T>((_, reject) =>
                setTimeout(
                    () => reject(new Error(`${label} timeout`)),
                    timeoutMs,
                ),
            ),
        ]);
    } catch (error) {
        console.error(`[safeAwait ${label}]`, error);
        return null;
    }
}

async function initApp() {
    logStep("initApp() start");
    const auth = useAuth();
    const authed = await safeAwait(auth.isAuthenticated(), "isAuthenticated");
    logStep(`auth.isAuthenticated() = ${authed}`);
    if (authed) {
        ApiService.setHeader();
        ApiService.mountRequestInterceptor();
        ApiService.mount401Interceptor();
    }
    ApiService.mountResponseInterceptor();
    logStep("ApiService configured");

    void useAttribution().captureFromUrl();

    const app = createApp(App).use(IonicVue).use(router).use(i18n);
    logStep("Vue app created");

    app.config.errorHandler = (err, _instance, info) => {
        const e = err instanceof Error ? err : new Error(String(err));
        console.error(`[Vue errorHandler ${info}]`, e.message, e.stack);
    };

    router.onError((error) => {
        console.error("[router.onError]", error);
        if (!shouldHandleDynamicImportError(error)) {
            return;
        }

        const hasRetried =
            sessionStorage.getItem(DYNAMIC_IMPORT_RELOAD_KEY) === "1";
        if (hasRetried) {
            sessionStorage.removeItem(DYNAMIC_IMPORT_RELOAD_KEY);
            return;
        }

        sessionStorage.setItem(DYNAMIC_IMPORT_RELOAD_KEY, "1");
        logStep("dynamic import failed → reloading");
        window.location.reload();
    });

    const bundle = Capacitor.isNativePlatform()
        ? await safeAwait(LiveUpdate.getCurrentBundle(), "getCurrentBundle")
        : null;
    const version = bundle?.bundleId ?? null;

    logStep(`bundleId=${version}`);

    Sentry.init(
        {
            app,
            enabled: ["production", "acceptation"].includes(
                import.meta.env.MODE,
            ),
            dsn: "https://240798335ca454081422b7433086ba28@o4508692096614400.ingest.de.sentry.io/4508692100153424",
            // Set your release version, such as "getsentry@1.0.0"
            release: "sportspoule@" + version,
            // Set your dist version, such as "1"
            dist: "<dist>",
            integrations: [
                Sentry.browserTracingIntegration() as any,
                Sentry.replayIntegration() as any,
            ],
            tracesSampleRate: 0.2,

            // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
            tracePropagationTargets: [import.meta.env.VUE_APP_API_URL],

            // Capture Replay for 10% of all sessions,
            // plus for 100% of sessions with an error
            // Learn more at
            // https://docs.sentry.io/platforms/javascript/session-replay/configuration/#general-integration-configuration
            replaysSessionSampleRate: 0.1,
            replaysOnErrorSampleRate: 1.0,
        },
        SentryVue.init,
    );
    logStep("Sentry initialized");

    Clarity.init("ufv9b1h6te");
    logStep("Clarity initialized");
    const { loadConsent } = useConsent();
    await safeAwait(loadConsent(), "loadConsent", 3000);
    logStep("consent loaded");

    let mounted = false;
    const safeMount = (reason: string) => {
        if (mounted) {
            return;
        }
        mounted = true;
        logStep(`mounting (${reason})...`);
        try {
            app.mount("#app");
            logStep("app.mount() returned");
        } catch (error) {
            console.error("[app.mount failed]", error);
        }
    };

    logStep("waiting for router.isReady()...");
    router
        .isReady()
        .then(() => safeMount("router ready"))
        .catch((error) => {
            console.error("[router.isReady rejected]", error);
            safeMount("router fallback");
        });

    setTimeout(() => safeMount("timeout fallback"), 8000);
}

initApp().catch((error) => {
    console.error("[initApp() rejected]", error);
});
